<script setup>
import { computed, watchEffect, defineEmits } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import labelHandler from '@composable/labelHandler'

const emit = defineEmits(['labelsResolved'])

const {
  labels,
  getLabels,
  labelAction,
  labelName,
  labelBtnLoading,
  addLabel,
  labelModal,
  newLabelModal,
  deleteLabelModal,
  currentLabel,
  deleteLabel,
  editLabel
} = labelHandler()

// Initial api call
getLabels()

// calculating the percentage of customers
const sum = computed(() => {
  if (!labels.value) return 0
  const total = labels.value.reduce((cum, newVal) => {
    return cum + parseInt(newVal['customer_count'])
  }, 0)

  // avoid 0 / 0
  return total === 0 ? 0.001 : total
})

// Add a table
const store = useStore()
const router = useRouter()
const enterCustomers = (label) => {
  if (label['customer_count'] === 0)
    return store.dispatch('addToast', {
      type: 'error',
      message: 'در این گروه مشتری وجود ندارد.'
    })
  router.push({
    name: 'Customer',
    query: { label: label['id'], type: 4, labelName: label['name'] }
  })
}

// Resolving the page
const resolved = computed(() => !!labels.value)
watchEffect(() => {
  if (labels.value) emit('labelsResolved')
})
</script>

<template>
  <div class="label-wrapper">
    <section class="load-resolved" v-if="resolved">
      <div class="all-cities">
        <template v-for="label in labels" :key="label['id']">
          <a @click="enterCustomers(label)" class="city-row">
            <div class="first-col">
              <h4>{{ label['name'] }}</h4>
            </div>
            <h5>{{ label['customer_count'].toLocaleString() }}</h5>
            <div class="last-col">
              <h6>{{ ((label['customer_count'] / sum) * 100).toFixed(0) }}%</h6>
              <svg viewBox="0 0 512 512">
                <path
                  d="M174.6 252.89l205.16 -205.16c7.2,-7.21 7.2,-18.89 0,-26.1 -7.2,-7.2 -18.88,-7.2 -26.09,0l-221.43 221.44c-7.2,7.2 -7.2,18.88 0,26.09l221.43 221.43c7.33,7.08 19.01,6.87 26.09,-0.46 6.91,-7.15 6.91,-18.48 0,-25.63l-205.13 -205.13 0 -0.03c-0.82,-0.83 -1.33,-1.97 -1.33,-3.23 0,-1.26 0.51,-2.4 1.33,-3.22l-0.03 0z"
                />
              </svg>
            </div>
          </a>
          <hr />
        </template>
      </div>
      <div class="credit-buttons" style="margin: 15px 0 0">
        <a class="button button--visible" @click="labelModal = true">
          <span class="button__text">تنظیمات</span>
          <svg class="button__icon" viewBox="0 0 24 24">
            <path
              d="m13.12 24h-2.24c-.757 0-1.396-.567-1.486-1.32l-.239-1.876c-.477-.155-.937-.346-1.374-.569l-1.494 1.161c-.606.469-1.459.415-1.985-.126l-1.575-1.575c-.537-.521-.591-1.374-.122-1.979l1.161-1.495c-.224-.437-.415-.897-.569-1.374l-1.88-.239c-.75-.092-1.317-.731-1.317-1.488v-2.24c0-.757.567-1.396 1.32-1.486l1.876-.239c.155-.477.346-.937.569-1.374l-1.16-1.494c-.47-.606-.415-1.46.127-1.986l1.575-1.575c.521-.537 1.375-.59 1.979-.122l1.494 1.162c.437-.223.897-.414 1.375-.569l.239-1.88c.09-.75.729-1.317 1.486-1.317h2.24c.757 0 1.396.567 1.486 1.32l.239 1.876c.478.155.938.346 1.375.569l1.494-1.161c.607-.469 1.459-.415 1.985.127l1.575 1.575c.537.521.591 1.374.122 1.979l-1.161 1.495c.224.437.415.897.569 1.374l1.88.239c.749.091 1.316.73 1.316 1.487v2.24c0 .757-.567 1.396-1.32 1.486l-1.876.239c-.155.477-.346.937-.569 1.374l1.161 1.494c.47.606.415 1.459-.127 1.985l-1.575 1.575c-.521.537-1.375.592-1.979.122l-1.495-1.161c-.437.224-.897.415-1.374.569l-.239 1.88c-.091.75-.73 1.317-1.487 1.317zm-5.39-4.86c.083 0 .168.021.244.063.551.308 1.148.556 1.774.736.192.055.333.219.358.417l.28 2.2c.03.251.247.444.494.444h2.24c.247 0 .464-.193.493-.439l.281-2.204c.025-.198.166-.362.358-.417.626-.18 1.223-.428 1.774-.736.175-.098.393-.081.55.042l1.75 1.36c.201.156.483.143.655-.034l1.585-1.585c.181-.176.195-.458.039-.66l-1.36-1.75c-.123-.158-.14-.375-.042-.55.308-.551.556-1.148.736-1.774.055-.192.219-.333.417-.358l2.2-.28c.251-.031.444-.248.444-.495v-2.24c0-.247-.193-.464-.439-.493l-2.204-.281c-.198-.025-.362-.166-.417-.358-.18-.626-.428-1.223-.736-1.774-.098-.175-.082-.392.042-.55l1.36-1.75c.157-.202.143-.484-.033-.654l-1.585-1.585c-.175-.182-.458-.196-.66-.039l-1.75 1.36c-.159.123-.376.14-.551.042-.549-.308-1.146-.555-1.774-.736-.192-.055-.333-.219-.358-.417l-.28-2.2c-.031-.252-.248-.445-.495-.445h-2.24c-.247 0-.464.193-.493.439l-.281 2.204c-.025.198-.166.362-.358.418-.628.18-1.225.428-1.774.735-.175.099-.392.081-.551-.041l-1.75-1.36c-.202-.157-.483-.143-.654.033l-1.585 1.586c-.181.176-.195.458-.039.66l1.36 1.75c.123.158.14.375.042.55-.309.551-.556 1.148-.736 1.774-.055.192-.219.333-.417.358l-2.2.28c-.251.03-.444.247-.444.494v2.24c0 .247.193.464.439.493l2.204.281c.198.025.362.166.417.358.18.626.428 1.223.736 1.774.098.175.082.392-.042.55l-1.36 1.75c-.157.202-.143.484.033.654l1.585 1.585c.175.181.456.195.66.039l1.75-1.36c.091-.068.199-.104.308-.104z"
            />
            <path
              d="m12 17c-2.757 0-5-2.243-5-5s2.243-5 5-5 5 2.243 5 5-2.243 5-5 5zm0-9c-2.206 0-4 1.794-4 4s1.794 4 4 4 4-1.794 4-4-1.794-4-4-4z"
            />
          </svg>
        </a>
      </div>
    </section>
    <modal :is-open="labelModal" @modalClosed="labelModal = false">
      <div class="label-modal">
        <div class="credit-buttons" style="margin:20px 0">
          <a
            class="button button--modal"
            @click="
              () => {
                labelModal = false
                newLabelModal = true
                labelAction = 'add'
              }
            "
          >
            <svg class="button__icon" viewBox="0 0 512 512">
              <path
                d="m368 272h-224c-8.832031 0-16-7.167969-16-16s7.167969-16 16-16h224c8.832031 0 16 7.167969 16 16s-7.167969 16-16 16zm0 0"
              />
              <path
                d="m256 384c-8.832031 0-16-7.167969-16-16v-224c0-8.832031 7.167969-16 16-16s16 7.167969 16 16v224c0 8.832031-7.167969 16-16 16zm0 0"
              />
            </svg>
          </a>
        </div>
        <div class="organ">
          <template v-for="label in labels" :key="label.id">
            <div class="organ__row" v-if="!label['is_default']">
              <h5 class="organ__title" v-text="label['name']" />
              <!-- <i class="organ__vr"></i> -->
              <a
                class="organ__btn-edit"
                @click="
                  () => {
                    currentLabel = label
                    labelAction = 'edit'
                    labelName = label.name
                    labelModal = false
                    newLabelModal = true
                  }
                "
              >
                <svg viewBox="0 0 512 512">
                  <path
                    d="M180.36 73.78c0,-27.71 22.65,-50.35 50.35,-50.35l17.62 0 15.34 0 17.62 0c27.7,0 50.35,22.64 50.35,50.35l0 248.72 0 37.95 0 3.93c-21.37,37.05 -42.73,74.11 -64.1,111.16 -9.81,19.64 -14.65,15.07 -23.29,-0.44l-63.89 -111.23 0 -3.42 0 -24.99 0 -261.68zm125.01 283.59l0 -188.65 -98.74 0 0 188.19 49.37 86.12 49.37 -85.66zm-98.74 -214.92l98.74 0 0 -68.67c0,-13.23 -10.84,-24.07 -24.08,-24.07l-17.62 0 -15.34 0 -17.62 0c-13.24,0 -24.08,10.84 -24.08,24.07l0 68.67z"
                  />
                </svg>
              </a>
              <i class="organ__vr"></i>
              <a
                class="organ__btn-delete"
                @click="
                  () => {
                    currentLabel = label
                    labelModal = false
                    deleteLabelModal = true
                  }
                "
                >&times;</a
              >
            </div>
            <!-- <i class="organ__hr" v-if="!label['is_default']" /> -->
          </template>
          <!-- <spinner v-show="modalLoading" /> -->
        </div>
      </div>
    </modal>
    <modal
      :is-open="newLabelModal"
      @modalClosed="
        () => {
          newLabelModal = false
          labelModal = true
        }
      "
      height="200px"
    >
      <div class="label-modal" style="padding-top: 0px">
        <div class="label-forms">
          <base-input
            label="نام گروه"
            width="100%"
            animate-type="remove"
            star-label
            style="margin-bottom: 20px"
            v-model="labelName"
          />
        </div>
        <div class="credit-buttons buttons--filter">
          <button
            class="button button--filter"
            @click="
              () => {
                newLabelModal = false
                labelModal = true
              }
            "
          >
            <svg class="button__icon" viewBox="0 0 512 512">
              <path
                d="M357.1 154.91l-0.01 -0.01c-5.23,-5.23 -13.8,-5.24 -19.04,0l-82.05 82.05 -82.05 -82.05c-5.24,-5.24 -13.81,-5.23 -19.04,0l-0.01 0.01c-5.23,5.23 -5.24,13.8 0,19.04l82.05 82.05 -82.05 82.05c-5.24,5.24 -5.23,13.81 0,19.04l0.01 0.01c5.23,5.23 13.8,5.24 19.04,0l82.05 -82.05 82.05 82.05c5.24,5.24 13.81,5.23 19.04,0l0.01 -0.01c5.23,-5.23 5.24,-13.8 0,-19.04l-82.05 -82.05 82.05 -82.05c5.24,-5.24 5.23,-13.81 0,-19.04z"
              />
            </svg>
          </button>
          <button
            class="button button--filter button--accept"
            @click="
              () => {
                if (labelAction === 'add') addLabel()
                else if (labelAction === 'edit') editLabel()
              }
            "
          >
            <svg class="button__icon" viewBox="0 0 512 512">
              <path
                d="M394.58 154.91l0 -0.01c-5.24,-5.23 -13.8,-5.24 -19.04,0l-173.63 173.62 -65.45 -65.45c-5.24,-5.24 -13.8,-5.23 -19.04,0l0 0.01c-5.24,5.23 -5.24,13.8 0,19.04l74.97 74.97 0 0.01c5.24,5.23 13.81,5.24 19.05,0l183.14 -183.15c5.24,-5.24 5.24,-13.81 0,-19.04z"
              />
            </svg>
            <spinner v-if="labelBtnLoading" />
          </button>
        </div>
      </div>
    </modal>
    <modal-confirm
      :is-open="deleteLabelModal"
      :is-loading="labelBtnLoading"
      @modalconfirmclosed="deleteLabelModal = false"
      @modalconfirmcancelled="
        () => {
          deleteLabelModal = false
          labelModal = true
        }
      "
      @confirm="deleteLabel(currentLabel?.id)"
      :text="`آیا از حذف گروه &quot;${currentLabel?.name}&quot; مطمئن هستید؟`"
    />
  </div>
</template>

<style scoped lang="scss">
.label-wrapper {
  width: 100%;
  position: relative;
  margin: 0 0 40px;

  .all-cities {
    padding: 0;
    width: 96%;
    display: flex;
    border-radius: 30px;
    flex-direction: column;
    align-items: center;
    overflow: hidden;
    background: rgba(#000, 1);

    .city-row {
      width: 100%;
      padding: 0 10px;
      display: grid;
      align-items: center;
      grid-template-columns: repeat(3, 1fr);
      height: 60px;
      cursor: pointer;
      .first-col {
        display: flex;
        align-items: center;
        svg {
          margin-right: 11px;
          width: 22px;
          height: 22px;
          fill: rgba(#ddd, 0.5);
        }

        h4 {
          justify-self: start;
          line-height: 30px;
          font-size: 14px;
          text-align: right;
          padding-top: 2px;
          color: #eee;
          margin: 0 12px 0 0;
        }
      }

      h5 {
        // width: 33.33%;
        height: 30px;
        line-height: 30px;
        font-size: 16px;
        text-align: center;
        color: #ccc;
        margin: 0;
        padding-top: 3px;
        &::after {
          position: relative;
          bottom: 1px;
          content: 'نفر';
          font-size: 14px;
          margin-right: 5px;
          color: #777;
        }
      }

      .last-col {
        // width: 33.33%;
        display: flex;
        align-items: center;
        justify-content: flex-end;
        h6 {
          margin: 0;
          display: inline-block;
          color: #111;
          font-weight: bold;
          width: 50px;
          height: 30px;
          line-height: 30px;
          text-align: center;
          padding-top: 2px;
          border-radius: 20px;
          background-color: #c69d4e;
        }
        svg {
          margin: 0px 12px 0 0;
          fill: #555;
          width: 20px;
          height: 20px;
        }
      }
      i {
        width: 1px;
        height: 100%;
        background-color: #111;
      }
    }
    hr {
      width: 94%;
      margin: 0;
      border-top: 1px solid rgba(#fff, 0.1);
      border-radius: 10px;
      &:last-child {
        display: none;
      }
    }
  }
}

.label-modal {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0;
  padding: 0 0 20px;
  width: 100%;
  background-color: #eee;
  .buttons--filter {
    justify-content: space-between;
    margin: 0;
    padding: 0 20px;
  }
}
.label-forms {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 90%;
  margin: 0 0 10px;
}

.organ {
  display: flex;
  align-items: center;
  flex-direction: column;
  width: 100%;
}
.organ__row {
  cursor: pointer;
  display: grid;
  column-gap: 6px;
  grid-template-columns: 1fr 30px 2px 30px;
  align-items: center;
  width: 92%;
  background: linear-gradient(
    to right,
    #bbb 44px,
    #ddd 44px,
    #ddd 90px,
    #fff 90px
  );
  border-radius: 10px;
  padding: 8px;
  margin-bottom: 10px;
  height: 50px;
}
.organ__vr {
  height: 100%;
  width: 1px;
  // background-color: #c7c7c7;
  justify-self: center;
}
.organ__img {
  width: 40px;
  height: 40px;
  border-radius: 5px;
  pointer-events: none;
}
.organ__title {
  color: #111;
  font-size: 16px;
  width: 100%;
  text-align: right;
  pointer-events: none;
  // margin-right: 5px;
}
.organ__hr {
  width: 92%;
  height: 1px;
  background-color: #aaa;
  margin: 10px 0;
  &:last-child {
    display: none;
  }
}
.organ__btn-delete {
  width: 30px;
  height: 30px;
  line-height: 20px;
  font-size: 26px;
  padding: 4px 0 0;
  margin: 0;
  border-radius: 100%;
  color: #888;
  // background-color: #c8c8c8;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-weight: bold;
  position: relative;
  // top: 4px;
  // left: -5px;
}
.organ__btn-edit {
  border-radius: 100%;
  // background-color: #eee;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  width: 30px;
  height: 30px;
  transform: rotate(30deg);
  svg {
    fill: #777;
    width: 26px;
    height: 26px;
  }
}
.button--visible {
  width: 75%;
  border-radius: 20px;
  .button__icon {
    transform: rotate(30deg);
    width: 25px;
    height: 25px;
    fill: #777;
    margin-right: 8px;
  }
}
.button--modal {
  width: 92%;
  background-color: #fff;
}
.button--modal .button__icon {
  fill: #777;
}
</style>
